import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {Link, Navigate, Route, Routes, useLocation, useNavigate} from 'react-router-dom';
import './App.css';
import {Config, WebUrls as urls} from './utills/config';
import {getApiRequest} from './utills/requests';
import {demoAccounts, demoTransactions, demoStories} from './cabinet/demo';
import {Button, Empty, Icon} from './cabinet/ui';
import {Dashboard, PinDetails, ConnectionDetails, History, Login, Payment, Settings, Support} from './cabinet/pages';

const CabinetContext = createContext(null);
export const useCabinet = () => useContext(CabinetContext);
const isDemo = new URLSearchParams(window.location.search).get('demo') === '1';

function App() {
    const [accounts, setAccounts] = useState([]);
    const [selectedPin, setSelectedPin] = useState('');
    const [associated, setAssociated] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [extras, setExtras] = useState({transactions: [], stories: [], profile: null, failed: [], loading: true});
    const [demoRemoved, setDemoRemoved] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();
    const active = accounts.find(point => String(point.pin) === selectedPin) || accounts[0];

    // Keep preview mode explicit in every URL, including after a page reload.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (isDemo && params.get('demo') !== '1') {
            params.set('demo', '1');
            navigate({pathname: location.pathname, search: params.toString()}, {replace: true, state: location.state});
        }
    }, [location.pathname, location.search, location.state, navigate]);


    const reload = useCallback(async () => {
        setError('');
        try {
            if (isDemo) {
                const points = Object.values(demoAccounts).filter(point => !demoRemoved.includes(String(point.pin)));
                setAccounts(points);
                return points;
            }
            if (!Config.tgWindow.initData) {setAccounts([]); return [];}
            const data = await getApiRequest('');
            if (data.error || data.detail) {
                if (data.associated) setAssociated(data.associated);
                setAccounts([]);
                return [];
            }
            const points = Object.values(data).filter(item => item && typeof item === 'object' && item.pin != null);
            setAccounts(points);
            return points;
        } catch (err) {setError(err.message); throw err;}
        finally {setLoading(false);}
    }, [demoRemoved]);

    const loadExtras = useCallback(async () => {
        if (isDemo) {
            setExtras({transactions: demoTransactions, stories: demoStories, profile: {first_name: 'Александр'}, failed: [], loading: false});
            return;
        }
        setExtras(previous => ({...previous, loading: true}));
        const names = ['transactions', 'stories', 'profile'];
        const results = await Promise.allSettled(['/transactions', '/stories', '/me'].map(path => getApiRequest(path).then(data => {
            if (data.error || data.detail) throw new Error('Данные недоступны');
            return data;
        })));
        const value = {transactions: [], stories: [], profile: null, failed: [], loading: false};
        results.forEach((result, index) => {
            if (result.status === 'rejected') value.failed.push(names[index]);
            else value[names[index]] = index === 2 ? result.value : Object.values(result.value || {});
        });
        setExtras(value);
    }, []);

    useEffect(() => {reload().catch(() => {});}, [reload]);
    const hasAccounts = accounts.length > 0;
    useEffect(() => {if (hasAccounts) loadExtras();}, [hasAccounts, loadExtras]);
    useEffect(() => {
        const tg = Config.tgWindow;
        tg.ready?.();
        if (tg.isVersionAtLeast?.('6.1')) {tg.setHeaderColor?.('#101217'); tg.setBackgroundColor?.('#101217');}
        tg.MainButton?.hide();
    }, []);
    useEffect(() => {
        const back = () => {
            const pin = new URLSearchParams(location.search).get('pin');
            const nested = [urls.AccountPage, urls.PaymentPage, urls.Settings, urls.History].includes(location.pathname);
            navigate(nested && pin ? `/v2/pin?pin=${encodeURIComponent(pin)}` : urls.UserPage);
        };
        const tg = Config.tgWindow;
        if (location.pathname !== urls.UserPage && location.pathname !== urls.BASE_URL && location.pathname !== urls.LoginPage) {
            tg.BackButton?.show(); tg.BackButton?.onClick(back);
        } else tg.BackButton?.hide();
        window.scrollTo(0, 0);
        return () => {tg.BackButton?.offClick(back); tg.BackButton?.hide();};
    }, [location.pathname, location.search, navigate]);

    const logout = async (pin, telegramId) => {
        if (isDemo) {
            if (!telegramId) setDemoRemoved(values => [...values, String(pin)]);
            return;
        }
        const data = await getApiRequest('/logout', {pin, ...(telegramId ? {telegram_id: telegramId} : {})});
        if (data.error || data.detail) throw new Error(String(data.error || data.detail));
        if (!telegramId) await reload();
    };
    const context = {accounts, active, selectedPin, setSelectedPin, associated, setAssociated, extras, reload, loadExtras, isDemo, logout};
    const user = isDemo ? {first_name: 'Александр'} : Config.user;
    const [failedAvatar, setFailedAvatar] = useState('');
    const avatarUrl = user.photo_url;
    const showAvatar = avatarUrl && avatarUrl !== failedAvatar;
    const guarded = page => hasAccounts ? page : <Navigate to={urls.LoginPage} replace/>;

    return <CabinetContext.Provider value={context}><div className="app-shell">
        <div className="workspace">
            <header className="topbar"><Link to={urls.UserPage} className="topbar-label">Личный кабинет <span>/</span> <b>Мой CLN</b></Link><Link to={urls.UserPage} className="mobile-title">Личный кабинет</Link><div className="profile"><span>{user.first_name || 'Мой профиль'}</span><span className="avatar">{showAvatar ? <img src={avatarUrl} alt="Ваш аватар" onError={() => setFailedAvatar(avatarUrl)}/> : (user.first_name?.[0] || <Icon name="user" size={19}/>)}</span></div></header>
            <main id="main-content">
                {isDemo && <div className="demo-banner"><span><span className="status-dot"/> Демо · вымышленные данные, платежи отключены</span><a href={urls.BASE_URL}>Выйти <Icon name="close" size={14}/></a></div>}
                {loading ? <div className="loading-state" role="status"><span className="loader"/><h2>Подключаем ваш кабинет</h2><p className="muted">Загружаем аккаунты и услуги</p></div> : error ? <div className="panel"><Empty title="Не удалось загрузить кабинет" icon="globe"><p>{error}</p></Empty><Button onClick={() => {setLoading(true); reload().catch(() => {});}}>Попробовать снова</Button></div> : <Routes>
                    <Route path={urls.BASE_URL} element={<Navigate to={hasAccounts ? urls.UserPage : urls.LoginPage} replace/>}/>
                    <Route path={urls.UserPage} element={guarded(<Dashboard/>)}/>
                    <Route path="/v2/pin" element={guarded(<PinDetails/>)}/>
                    <Route path="/v2/connections" element={<Navigate to={urls.UserPage} replace/>}/>
                    <Route path={urls.AccountPage} element={guarded(<ConnectionDetails/>)}/>
                    <Route path={urls.History} element={guarded(<History/>)}/>
                    <Route path={urls.PaymentPage} element={guarded(<Payment/>)}/>
                    <Route path={urls.Settings} element={guarded(<Settings/>)}/>
                    <Route path={urls.LoginPage} element={<Login/>}/>
                    <Route path={urls.Support} element={<Support/>}/>
                    <Route path="*" element={<Navigate to={urls.BASE_URL} replace/>}/>
                </Routes>}
                <footer className="page-footer"><span>CLN · Интернет, который рядом</span><span><Icon name="shield" size={14}/> Личный кабинет абонента</span></footer>
            </main>
        </div>
    </div></CabinetContext.Provider>;
}
export default App;
